import React, { useEffect, useState } from 'react';
import { BASE_URL } from '../config/api'; // Assuming BASE_URL is correctly defined in your config/api.js
import { useNavigate } from 'react-router-dom';
import jsPDF from 'jspdf'; // Import jsPDF for PDF export
import 'jspdf-autotable'; // Import for table support in PDF
import * as XLSX from 'xlsx'; // Import for Excel export

const CatalogueList = () => {
    const [catalogues, setCatalogues] = useState([]);
    const [categories, setCategories] = useState([]);
    const [subCategories, setSubCategories] = useState([]);
    const [filteredCatalogues, setFilteredCatalogues] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const navigate = useNavigate();

    // Fetch catalogues, categories, and subcategories when the component mounts
    useEffect(() => {
        // Fetch categories
        fetch(`${BASE_URL}categories`)
            .then(response => response.json())
            .then(data => {
                if (data.status === 1) {
                    setCategories(data.data); // Store the category data
                } else {
                    alert('Error fetching categories');
                }
            })
            .catch(error => console.error('Error fetching categories:', error));

        // Fetch subcategories
        fetch(`${BASE_URL}subcategories`)
            .then(response => response.json())
            .then(data => {
                if (data.status === 1) {
                    setSubCategories(data.data); // Store the subcategory data
                } else {
                    alert('Error fetching subcategories');
                }
            })
            .catch(error => console.error('Error fetching subcategories:', error));

        // Fetch catalogues
        fetch(`${BASE_URL}Catalogue`)
            .then(response => response.json())
            .then(data => {
                if (data.status === 1) {
                    setCatalogues(data.data); // Store the catalogue data
                    setFilteredCatalogues(data.data); // Set initial filtered catalogues
                } else {
                    alert('Error fetching catalogues');
                }
            })
            .catch(error => console.error('Error fetching catalogues:', error));
    }, []);

    // Handle search term change
    const handleSearchChange = (event) => {
        const term = event.target.value;
        setSearchTerm(term);

        // Filter catalogues based on the search term
        const filtered = catalogues.filter(catalogue =>
            catalogue.id.toString().includes(term) ||
            catalogue.image.toLowerCase().includes(term.toLowerCase())
        );
        setFilteredCatalogues(filtered);
    };

    // Get category name by category_id
    const getCategoryName = (categoryId) => {
        const category = categories.find(cat => cat.id === categoryId);
        return category ? category.name : 'N/A';
    };

    // Get subcategory name by subcategory_id
    const getSubCategoryName = (subCategoryId) => {
        const subCategory = subCategories.find(sub => sub.id === subCategoryId);
        return subCategory ? subCategory.name : 'N/A';
    };

    // Navigate to the edit catalogue page
    const handleEdit = (id) => {
        navigate(`/EditCatalogue/${id}`);
    };

    // Navigate to the add catalogue page
    const handleAdd = () => {
        navigate('/AddCatalogue');
    };

    // Handle catalogue deletion
    const handleDelete = (id) => {
        if (window.confirm("Are you sure you want to delete this catalogue?")) {
            fetch(`${BASE_URL}Catalogue/${id}`, {
                method: 'DELETE',
                headers: {
                    'Content-Type': 'application/json',
                },
            })
                .then(response => response.json())
                .then(data => {
                    if (data.status === 1) {
                        // Remove the deleted catalogue from both lists (catalogues and filteredCatalogues)
                        setCatalogues(catalogues.filter(catalogue => catalogue.id !== id));
                        setFilteredCatalogues(filteredCatalogues.filter(catalogue => catalogue.id !== id));
                    }
                })
                .catch(error => console.error('Error deleting catalogue:', error));
        }
    };

    // Export filtered catalogues to PDF
    const handleExportPDF = () => {
        const doc = new jsPDF();

        // Add Title
        doc.text('Catalogue List', 14, 10); // Title at top of PDF

        // Prepare table data for PDF
        const tableData = filteredCatalogues.map(catalogue => [
            catalogue.id,
            getCategoryName(catalogue.category_id),
            getSubCategoryName(catalogue.sub_category_id),
            catalogue.image ? `${BASE_URL}${catalogue.image}` : 'No Image',
            
        ]);

        // Add table to PDF with autoTable
        doc.autoTable({
            head: [['ID', 'Category', 'Subcategory', 'Image']], // Table headers
            body: tableData,
        });

        // Save the PDF with a filename
        doc.save('catalogue_list.pdf');
    };

    // Export filtered catalogues to Excel
    const handleExportExcel = () => {
        const worksheet = XLSX.utils.json_to_sheet(filteredCatalogues.map(catalogue => ({
            ID: catalogue.id,
            Category: getCategoryName(catalogue.category_id),
            SubCategory: getSubCategoryName(catalogue.sub_category_id),
            Image: catalogue.image ? `${BASE_URL}${catalogue.image}` : 'No Image',
            Weight: catalogue.weight,
            CreatedAt: new Date(catalogue.created_at).toLocaleString(),
            UpdatedAt: new Date(catalogue.updated_at).toLocaleString(),
        })));
        
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Catalogue List");

        // Save the Excel file with a filename
        XLSX.writeFile(workbook, 'catalogue_list.xlsx');
    };

    return (
        <div id="main">
            <header className="mb-3">
                <a href="#" className="burger-btn d-block d-xl-none">
                    <i className="bi bi-justify fs-3"></i>
                </a>
            </header>

            <div className="page-heading">
                <div className="page-title">
                    <div className="row">
                        <div className="col-12 col-md-6 order-md-1 order-last">
                            <h3>Forming Catalogue List</h3>
                        </div>
                        <div className="col-12 col-md-6 order-md-2 order-first">
                            <nav aria-label="breadcrumb" className="breadcrumb-header float-start float-lg-end">
                                <ol className="breadcrumb"></ol>
                            </nav>
                        </div>
                    </div>
                </div>
            </div>

            <section className="section">
                <div className="row" id="basic-table">
                    <div className="col-12 col-md-12">
                        <div className="card">
                            <div className="card-header">
                                <div className="row">
                                    <div className="col-md-6">
                                        <input
                                            type="text"
                                            className="form-control"
                                            placeholder="Search Catalogue"
                                            value={searchTerm}
                                            onChange={handleSearchChange}
                                        />
                                    </div>
                                    <div className="col-md-6 text-end">
                                        <button className="btn btn-primary" onClick={handleAdd}>Add Catalogue</button>
                                        <button className="btn btn-secondary ms-2" onClick={handleExportPDF}>Export to PDF</button>
                                        {/* <button className="btn btn-secondary ms-2" onClick={handleExportExcel}>Export to Excel</button> */}
                                    </div>
                                </div>
                            </div>
                            <div className="card-body">
                                <div className="table-responsive">
                                    <table className="table table-lg">
                                        <thead>
                                            <tr>
                                                <th>ID</th>
                                                <th>Subcategory</th>
                                                <th>Image</th>
                                                {/* <th>Weight</th> */}
                                                {/* <th>Created At</th>
                                                <th>Updated At</th> */}
                                                <th className="text-end">Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {filteredCatalogues.map(catalogue => (
                                                <tr key={catalogue.id}>
                                                    <td>{catalogue.id}</td>
                                                    <td>{getSubCategoryName(catalogue.sub_category_id)}</td>
                                                    <td>
                                                        <img
                                                            src={`${BASE_URL}${catalogue.image}`}
                                                            alt="Catalogue"
                                                            width="100"
                                                            height="100"
                                                        />
                                                    </td>
                                                    {/* <td>{catalogue.weight}</td> */}
                                                    {/* <td>{new Date(catalogue.created_at).toLocaleString()}</td>
                                                    <td>{new Date(catalogue.updated_at).toLocaleString()}</td> */}
                                                    <td className="text-end">
                                                        <button className="btn btn-success" onClick={() => handleEdit(catalogue.id)}>Edit</button>
                                                        <button className="btn btn-danger ms-2" onClick={() => handleDelete(catalogue.id)}>Delete</button>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default CatalogueList;
